import * as Yup from 'yup';
import { endOfDay } from 'date-fns';

/**
 * The one frontend email check, shared by every email field so they can't disagree. At least as strict
 * as the backend's IsEmail (validator.js): it requires a dotted domain with a 2+ letter TLD, which
 * Yup's own `.email()` doesn't (that one accepts TLD-less addresses the API then rejects).
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+(\.[^\s@]+)*\.[A-Za-z]{2,}$/;
export const isValidEmail = (value: string): boolean => EMAIL_PATTERN.test(value.trim());

/** Yup wrapper around the shared check, for schema-based forms. Empty values pass — pair with `.required()`. */
export const emailSchema = (message = 'Must be a valid email'): Yup.StringSchema<string | undefined> =>
  Yup.string().test('email', message, value => value == null || value === '' || isValidEmail(value));

export const ReportSchema = Yup.object().shape(
  {
    productId: Yup.string().nullable().required('Product is required'),
    userId: Yup.string().nullable().required('Member is required'),
    today: Yup.string().required('Description is required'),
    hours: Yup.number()
      .nullable()
      .when('customHours', {
        is: (customHours: number) => !customHours,
        then: schema => schema.nullable().typeError('Time is required').min(0.1).max(24).required('Time is required'),
        otherwise: schema => schema.nullable(),
      }),
    customHours: Yup.number()
      .nullable()
      .when('hours', {
        is: (hours: number) => !hours && hours !== 0,
        then: schema => schema.nullable().required('Time is required'),
        otherwise: schema => schema.nullable(),
      }),
    workday: Yup.date()
      .typeError('Invalid date')
      .required('Workday is required')
      // Must recompute at validation time: `.max(endOfDay(new Date()))` is frozen at module load and breaks after midnight or a long session.
      .test('not-future', 'Date cannot be in the future', value => {
        if (!value) return true;
        return value.getTime() <= endOfDay(new Date()).getTime();
      }),
    role: Yup.string().required('Role is required'),
  },
  [['hours', 'customHours']],
);

export const OrganizationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
});

export const SendSprintSchema = Yup.object().shape({
  emails: Yup.array()
    .of(
      Yup.object().shape({
        value: emailSchema('Must be a valid email'),
      }),
    )
    .min(1, 'At least 1 email address'),
});
